import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { flexCenterAll } from '../../styles/mixins';
import { transition } from '../../styles/animations';
import { hoverEase } from '../../styles/variables';
import HBText from '../HBText/HBText';
import { StyledTrigger } from '../HBMenu/HBMenu.styles';
import HBClickable from '../HBClickable/HBClickable';

export const tabTitleStyle = {
    sm: {
        active: 'emphasis14',
        notActive: 'body14'
    },
    md: {
        active: 'emphasis16',
        notActive: 'body16'
    },
    lg: {
        active: 'emphasis18',
        notActive: 'body18'
    }
} as const;

export const Tabs = styled.div`
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
`;

export const TabTitle = styled(HBText).attrs({
    color: 'text.primary'
})`
    ${transition('all', ...hoverEase)};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &::after {
        // for measuring width when text is bold
        display: block;
        content: attr(data-text);
        font-weight: 600;
        height: 0px;
        // all 3 methods below make sure this is not visible:
        color: transparent;
        overflow: hidden;
        visibility: hidden;
    }
`;

const menuVisibleStyles = css`
    > i {
        transform: translate3d(-10px, 0, 0);
    }
    ${TabTitle} {
        font-weight: 600;
        transform: translate3d(-10px, 0, 0);
    }
    ${StyledTrigger} {
        opacity: 1 !important;
        margin-left: 8px;
    }
`;

export const Tab = styled(HBClickable)<{
    $hasMenu: boolean;
    $isActive: boolean;
    isDisabled: boolean;
    $isIconOnly: boolean;
    $isMenuOpen: boolean;
    $maxTabWidth?: number;
    $selectedHighlightColor: string;
    $stretchToFill: boolean;
}>`
    position: relative;
    cursor: pointer;
    ${flexCenterAll}
    padding: 0 16px 12px;
    border-bottom: 3px solid transparent;
    ${transition('all', ...hoverEase)};

    ${({ $maxTabWidth, $stretchToFill }) => css`
        ${$maxTabWidth != null &&
        css`
            max-width: ${$maxTabWidth}px;
        `}
        ${$stretchToFill &&
        css`
            flex: 1;
        `}
    `}

    > i {
        margin-right: ${({ $isIconOnly }) => ($isIconOnly ? 'unset' : '8px')};
        ${transition('all', ...hoverEase)};
        line-height: ${({ $isIconOnly }) =>
            $isIconOnly
                ? '0' // should use height of the icon
                : '1'}; // should use height of the text
        svg * {
            ${transition('fill', ...hoverEase)};
            fill: ${vars.colors.text.primary};
        }
    }

    ${({ isDisabled, $isIconOnly }) =>
        isDisabled &&
        css`
            cursor: default;
            ${TabTitle} {
                color: ${vars.colors.text.disabled};
            }
            > i svg * {
                fill: ${vars.colors.text.disabled};
            }
        `}

    ${({
        isDisabled,
        $hasMenu,
        $isMenuOpen,
        $isActive,
        $selectedHighlightColor
    }) =>
        !isDisabled &&
        css`
            &:hover,
            &:focus {
                ${TabTitle} {
                    font-weight: 600;
                }
                > i svg * {
                    fill: ${vars.colors.text.primary};
                }
            }

            ${$isActive &&
            css`
                border-bottom: 3px solid
                    ${$selectedHighlightColor || vars.colors.base.accent};
                > i svg * {
                    fill: ${vars.colors.text.primary};
                }
            `}

            ${$hasMenu &&
            css`
                &:hover,
                &:focus-visible,
                &:focus-within {
                    ${menuVisibleStyles}
                }

                ${$isMenuOpen &&
                css`
                    ${menuVisibleStyles}
                `}
            `}
        `}
`;
